import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import jwt_decode from 'jwt-decode';
import api, { GlobalApiInterceptors } from '../services/api';

const AuthContext = createContext({});

function AuthProvider({ children }) {
  const history = useHistory();
  const [carregandoPermissoes, setCarregandoPermissoes] = useState(false);
  const [permissoes, setPermissoes] = useState([]);
  const [navegarMenu, setNavegarMenu] = useState(true);
  const [data, setData] = useState(() => {
    const access_token = localStorage.getItem('@TmovGR:access_token');
    const refresh_token = localStorage.getItem('@TmovGR:refresh_token');
    const expiry_date_refresh_token = localStorage.getItem(
      '@TmovGR:expiry_date_refresh_token'
    );
    const id_token = localStorage.getItem('@TmovGR:id_token');
    const usuario = localStorage.getItem('@TmovGR:usuario');
    const grupos = localStorage.getItem('@TmovGR:grupos');

    if (
      access_token &&
      refresh_token &&
      id_token &&
      expiry_date_refresh_token &&
      usuario
    ) {
      return {
        access_token,
        refresh_token,
        id_token,
        expiry_date_refresh_token,
        usuario: JSON.parse(usuario),

        grupos: grupos ? JSON.parse(grupos) : null,
      };
    }

    return {};
  });

  const signIn = useCallback(async ({ username, password }) => {
    const formData = {
      usuario: username,
      senha: password,
    };

    delete api.defaults.headers.authorization;
    delete api.defaults.headers.userinfo;

    const response = await api.post('/login', formData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const {
      accessToken,
      refreshToken,
      expiresInDate,
      idUsuario,
      login,
      nome,
    } = response.data;

    const usuario = { nome, login, avatar_url: '' };

    const expiry_token = moment(expiresInDate).format();

    localStorage.setItem('@TmovGR:access_token', accessToken);
    localStorage.setItem('@TmovGR:idUsuario', idUsuario);
    localStorage.setItem('@TmovGR:refresh_token', refreshToken);
    localStorage.setItem('@TmovGR:expiry_date_refresh_token', expiry_token);
    localStorage.setItem('@TmovGR:id_token', accessToken);
    localStorage.setItem('@TmovGR:usuario', JSON.stringify(usuario));

    setData({
      accessToken,
      refreshToken,
      expiry_date_refresh_token: expiry_token,
      usuario,
    });
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = localStorage.getItem('@TmovGR:refresh_token');

    try {
      const refresh_Token = { refreshToken };
      await api.post('/logout', refresh_Token, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      delete api.defaults.headers.authorization;
      delete api.defaults.headers.userinfo;
      setData({});
      localStorage.clear();
      history.push('/');
    } catch (err) {
      console.error('logout', err);
    }
  }, [history]);

  const refreshToken = useCallback(async () => {
    const getRefreshToken = localStorage.getItem('@TmovGR:refresh_token');

    const formData = new URLSearchParams();

    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', getRefreshToken);

    const response = await api.post('/tmov-gr/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    const {
      access_token,
      refresh_token,
      id_token,
      expiry_date_refresh_token,
    } = response.data;

    localStorage.setItem('@TmovGR:access_token', access_token);
    localStorage.setItem('@TmovGR:refresh_token', refresh_token);
    localStorage.setItem(
      '@TmovGR:expiry_date_refresh_token',
      expiry_date_refresh_token
    );
    localStorage.setItem('@TmovGR:id_token', id_token);

    const usuario = localStorage.getItem('@TmovGR:usuario');

    setData({
      access_token,
      refresh_token,
      id_token,
      expiry_date_refresh_token,
      usuario: usuario ? JSON.parse(usuario) : {},
    });

    return { access_token, id_token };
  }, []);

  const atualizarUsuario = useCallback(({ nome, avatar_url, login }) => {
    const usuario = { nome, avatar_url, login };

    localStorage.setItem('@TmovGR:usuario', JSON.stringify(usuario));

    setData(dataOld => ({
      ...dataOld,
      usuario,
    }));
  }, []);

  const buscarPermissoes = useCallback(async () => {
    const access_token = localStorage.getItem('@TmovGR:access_token');
    if (access_token) {
      setCarregandoPermissoes(true);
      const _permissoes = jwt_decode(access_token)?.resource_access[
        'api-tmov-gr'
      ]?.roles;

      setPermissoes(_permissoes);
      setCarregandoPermissoes(false);
      return _permissoes || [];
    }
  }, []);

  const updateNavegarMenu = useCallback(async value => {
    setNavegarMenu(value);
  }, []);

  const temPermissao = useCallback(
    (permissoesSistema = []) => {
      if (!Array.isArray(permissoesSistema)) {
        return false;
      }

      if (!(permissoes?.length || 0) === 0) return false;

      const comparaPermissoes = permissoes.some(permissao =>
        permissoesSistema.includes(permissao)
      );

      return comparaPermissoes;
    },
    [permissoes]
  );

  useEffect(() => {
    buscarPermissoes();
  }, [buscarPermissoes]);

  const authContextValue = useMemo(
    () => ({
      isAuthenticated: !!data.access_token,
      roles: [],
      access_token: data.access_token,
      signIn,
      signOut,
      atualizarUsuario,
      usuario: data.usuario
        ? data.usuario
        : { nome: null, avatar_url: null, login: null },
      carregandoPermissoes,
      permissoes,
      temPermissao,
      buscarPermissoes,
      navegarMenu,
      updateNavegarMenu,
    }),
    [
      atualizarUsuario,
      buscarPermissoes,
      carregandoPermissoes,
      data.access_token,
      data.usuario,
      navegarMenu,
      permissoes,
      signIn,
      signOut,
      temPermissao,
      updateNavegarMenu,
    ]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      <GlobalApiInterceptors
        isAuthenticated={!!data.access_token}
        refreshToken={refreshToken}
        signOut={signOut}
      />
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);

  return context;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthProvider, useAuth };
